/**
 * 문장 가이드 — AI 글쓰기 도우미의 세 번째 모드 (§5.23).
 *
 * 영역·단계별 문장 프레임과 좋은 예를 제공한다. AI 호출 없이 쓰는 정적 콘텐츠라
 * ANTHROPIC_API_KEY가 없는 환경에서도 교사를 도울 수 있다.
 *
 * 문장 원칙(§6.9): 아이 행동 중심의 긍정 서술형("~해요"), 판정·비교 어휘 금지.
 * 이 파일의 모든 문구는 `writing-guide.test.ts`가 금칙어 검사를 통과하는지 확인한다.
 * "○○" 자리에는 아이 이름이 들어간다.
 */
/** 7단계 축(§6.4)을 서술로 옮길 때의 힌트 — ZPD의 "함께 → 도움받아 → 스스로" 이행 구조 */
export const WRITING_STAGE_HINTS = [
    {
        levels: 'L1–L2 씨앗·새싹',
        hint: '관심을 보이고 탐색하는 모습을 담아요. "바라봐요", "손을 뻗어요", "궁금해해요"처럼 시작의 신호를 적어 주세요.',
    },
    {
        levels: 'L3–L5 떡잎·줄기·꽃봉오리',
        hint: '함께 해 보고, 도움을 받아 해내는 과정을 담아요. "선생님과 함께", "손을 잡고", "몇 번의 시도 끝에"처럼 이행의 순간을 적어 주세요.',
    },
    {
        levels: 'L6–L7 꽃·열매',
        hint: '스스로 해내고 넓혀 가는 모습을 담아요. "혼자서", "먼저 나서서", "친구에게 알려 줘요"처럼 확장의 장면을 적어 주세요.',
    },
];
export const WRITING_GUIDES = [
    {
        domainCode: 'PHYSICAL_GROSS',
        frames: [
            '○○이는 (장소·활동)에서 (몸의 움직임)을 즐겨요.',
            '(움직임)을 할 때 (변화한 모습)이 보여요.',
            '요즘은 (새로 시도하는 움직임)에 도전하고 있어요.',
        ],
        examples: [
            '하늘이는 바깥놀이에서 경사진 길을 오르내리는 걸 즐겨요. 몇 번이고 다시 올라가요.',
            '두 발 모아 뛰기를 할 때 무릎을 구부려 균형을 잡는 모습이 보여요.',
        ],
    },
    {
        domainCode: 'PHYSICAL_FINE',
        frames: [
            '○○이는 (도구·재료)를 손에 쥐고 (하는 일)을 해요.',
            '(활동)에서 손끝의 힘 조절이 점점 (변화)하고 있어요.',
        ],
        examples: [
            '서아는 크레파스를 손에 쥐고 동그란 선을 그려요. 종이 가득 그린 그림을 보여 주며 웃어요.',
            '단추 끼우기 놀이에서 손끝의 힘 조절이 점점 섬세해지고 있어요.',
        ],
    },
    {
        domainCode: 'COMMUNICATION',
        frames: [
            '○○이는 (상황)에서 (말·몸짓·표정)으로 마음을 표현해요.',
            '(낱말·문장)을 새로 말하기 시작했어요.',
            '이야기를 들을 때 (반응하는 모습)을 보여요.',
        ],
        examples: [
            '하늘이는 그림책을 보다가 "멍멍!" 하고 소리 내며 강아지 그림을 가리켜요.',
            '"더 주세요"라고 두 단어를 이어 말하기 시작했어요.',
        ],
    },
    {
        domainCode: 'COGNITION',
        frames: [
            '○○이는 (사물·현상)을 (탐색하는 방식)으로 알아 가요.',
            '(놀이)에서 (발견·비교·순서)를 알아차려요.',
            '궁금한 것이 있으면 (하는 행동)을 해요.',
        ],
        examples: [
            '도윤이는 블록을 크기 순서대로 늘어놓으며 "크다, 작다"를 비교해요.',
            '물놀이에서 컵의 물을 이리저리 옮겨 부으며 양의 변화를 알아 가요.',
        ],
    },
    {
        domainCode: 'SOCIAL_EMOTION',
        frames: [
            '○○이는 (친구·선생님)에게 (다가가는 방식)으로 마음을 나눠요.',
            '(감정 상황)에서 (자기만의 방식)으로 마음을 다스려요.',
            '놀이에서 (역할·차례)를 경험하고 있어요.',
        ],
        examples: [
            '서아는 우는 친구 곁에 가만히 앉아 손수건을 건네요.',
            '차례를 기다리는 게 아직 어렵지만, 선생님 손을 잡고 기다리는 연습을 하고 있어요.',
        ],
    },
    {
        domainCode: 'SELF_CARE',
        frames: [
            '○○이는 (일과)에서 (스스로 하는 일)을 해내고 있어요.',
            '(생활 습관)이 조금씩 몸에 배어 가요.',
        ],
        examples: [
            '하늘이는 점심시간에 숟가락을 스스로 쥐고 먹어요. 흘려도 다시 떠 보며 연습해요.',
            '낮잠 후 이불을 개는 흉내를 내며 정리에 관심을 보여요.',
        ],
    },
];
/** 영역 코드로 가이드를 찾는다. 없는 코드는 undefined — 화면이 감춘다. */
export function writingGuideFor(domainCode) {
    return WRITING_GUIDES.find((g) => g.domainCode === domainCode);
}
//# sourceMappingURL=writing-guide.js.map